import { SchemaBooleanBaseIcon } from '@tableau/icons/SchemaBooleanBaseIcon';
import { SchemaCalendarBaseIcon } from '@tableau/icons/SchemaCalendarBaseIcon';
import { SchemaCalendarClockDefaultIcon } from '@tableau/icons/SchemaCalendarClockDefaultIcon';
import { SchemaNumberBaseIcon } from '@tableau/icons/SchemaNumberBaseIcon';
import { SchemaTextBaseIcon } from '@tableau/icons/SchemaTextBaseIcon';
import React from 'react';

import { assertUnreachable } from '~commonInfra/utils/TypescriptHelpers';
import { SessionServerSelectors, SessionSiteSelectors } from '~src/bootstrap/redux/selectors/SessionInfoSelectors';
import { getAppStore } from '~src/bootstrap/redux/store/AppStore';
import { Messages } from '~src/codegen/Localize';
import { FlowOutputStepInfo } from '~src/flow/models/FlowOutputStepInfo';

import { FlowParameter, FlowParameterParams,FlowParameterRun,FlowParametersState, FlowParameterType } from '../types/FlowParameters';

export function getIconForParameterType(type: FlowParameterType, className?: string): JSX.Element | null {
  switch(type) {
    case 'boolean':
      return <SchemaBooleanBaseIcon size={20} state='Discrete' alt='' className={className} />;
    case 'date':
      return <SchemaCalendarBaseIcon size={20} state='Discrete' alt='' className={className} />;
    case 'datetime':
    case 'timestamp':
      return <SchemaCalendarClockDefaultIcon size={20} state='Discrete' alt='' className={className} />;
    case 'real':
    case 'integer':
      return <SchemaNumberBaseIcon size={20} state='Discrete' alt='' className={className} />;
    case 'string':
      return <SchemaTextBaseIcon size={20} state='Discrete' alt='' className={className} />;
    default:
      assertUnreachable(type);
      return null;
  }
}

// Temporary function to make development easier
export function temporaryParametersForTesting(): FlowParametersState {
  return {
    requiredParameters: [
      {id: '0', name: 'Boolean Parameter', isRequired: true, domain: {type: 'boolean', domainType: 'binary'}, description: 'boolean parameter'},
      {id: '1', name: 'Text Parameter', isRequired: true, domain: {type: 'string', domainType: 'all'}, description: 'This is a description for Parameter A'},
      {id: '2', name: 'Integer Parameter', isRequired: true, domain: {type: 'integer', domainType: 'all'}, description: 'integer parameter'},
      {id: '3', name: 'Float Parameter', isRequired: true, domain: {type: 'real', domainType: 'all'}, description: 'float parameter'},
      {id: '4', name: 'Date Parameter', isRequired: true, domain: {type: 'date', domainType: 'all'}, description: 'date parameter'},
    ],
    additionalParameters: [
      {id: '5', name: 'Additional Parameter 1', isRequired: false, domain: {type: 'string', domainType: 'all'}, description: '1sd9fh'},
      {id: '6', name: 'Additional Parameter 2', isRequired: false, domain: {type: 'string', domainType: 'all'}, description: '19023'},
    ],
    systemParameters: [
      {id: '7', value: 'YYYY-MM-DD', name: 'YYYY-MM-DD', isRequired: false, domain: {type: 'timestamp', domainType: 'system'}},
      {id: '8', value: 'YYYYMMDD_HHMMSS', name: 'YYYYMMDD_HHMMSS', isRequired: false, domain: {type: 'timestamp', domainType: 'system'}},
    ],
  };
}

export function getFlowParameterInputPlaceholder(type: FlowParameterType) {
  switch(type) {
    case 'boolean':
      return Messages.flowParametersInputBooleanValue();
    case 'date':
    case 'datetime':
      return Messages.flowParametersInputDateValue();
    case 'integer':
      return Messages.flowParametersInputIntegerValue();
    case 'real':
      return Messages.flowParametersInputNumberValue();
    case 'string':
      return Messages.flowParametersInputStringValue();
    case 'timestamp':
      return '';
    default:
      assertUnreachable(type);
  }
}

// Separate a list of parameters into required, additional and system
export function separateParameters(parameters: FlowParameter[]) {
  const requiredParameters: FlowParameter[] = [];
  const additionalParameters: FlowParameter[] = [];
  const systemParameters: FlowParameter[] = [];
  parameters.forEach(parameter => {
    if (parameter) {
      if (parameter.domain.domainType === 'system') {
        systemParameters.push(parameter);
      } else if (parameter.isRequired) {
        requiredParameters.push(parameter);
      } else {
        additionalParameters.push(parameter);
      }
    }
  });
  return {
    requiredParameters,
    additionalParameters,
    systemParameters,
  };
}

// Checks if feature flag is on, if the instance is enabled on server or is online, and if the setting is enabled on the site, or is a single server-site
export function areFlowParametersEnabled() {
  const state = getAppStore().getState();
  const isOnline = SessionServerSelectors.isOnlineEnvironment(state);
  const doesTheServerHaveMoreThanOneSite = SessionServerSelectors.doesTheServerHaveMoreThanOneSite(state);
  const isEnabledOnServerOrOnline = SessionServerSelectors.flowParametersEnabled(state) || isOnline;
  const isEnabledOnSite = SessionSiteSelectors.flowParametersEnabled(state);
  return isEnabledOnServerOrOnline && (isEnabledOnSite || (!isOnline && !doesTheServerHaveMoreThanOneSite));
}

// Merge arrays of parameters and dedupe to unique parameters
export function getParametersFromOutputSteps(outputSteps: FlowOutputStepInfo[]): FlowParameter[] {
  let parameters: FlowParameter[] = [];
  outputSteps.forEach(outputStep => {
    if (outputStep && outputStep.relevantParameters) {
      parameters = parameters.concat(outputStep.relevantParameters);
    }
  });
  return parameters.filter((parameter, index, array) => {
    return index === array.findIndex(p => p.id === parameter.id);
  });
}

export function getParameterParamsFromParameterRuns(parameterRuns: FlowParameterRun[]): FlowParameterParams[] {
  return parameterRuns.map(run => {
    return {
      id: run.parameterId,
      overrideValue: run.overrideValue
    };
  });
}

export function parameterMapToArray(parameterMap: {[key: string]: string}): FlowParameterParams[] {
  const parameterKeys =  Object.keys(parameterMap);
  return parameterKeys.length > 0 ? parameterKeys.map(key => {
    return {
      id: key,
      overrideValue: parameterMap[key]
    };
  }) : [];
}

export function parameterParamsArrayToMap(parameterArray: FlowParameterParams[]): {[key: string]: string} {
  const map: {[key: string]: string} = {};
  parameterArray.forEach(parameter => {
    map[parameter.id] = parameter.overrideValue;
  });
  return map;
}

export function parameterArrayToMap(parameterArray: FlowParameter[]): {[key: string]: string} {
  const map: {[key: string]: string} = {};
  parameterArray.forEach(parameter => {
    map[parameter.id] = parameter.value;
  });
  return map;
}

export function isFlowParameterRun(parameter: FlowParameter | FlowParameterRun): parameter is FlowParameterRun {
  return (parameter as any).id === undefined;
}

export function containsAnyTypeParameter(parameters: FlowParameter[]) {
  return parameters.find(parameter => parameter.domain.domainType === 'all');
}

export function getFlowTimestampTypeName(value: string): string | null {
  switch(value) {
    case 'YYYY-MM-DD':
      return Messages.flowParametersTimestampFormat_YYYY_MM_DD();
    case 'YYYY-MM-DD_HH-MM-SS':
      return Messages.flowParametersTimestampFormat_YYYY_MM_DD_HH_MM_SS();
    case 'YYYYMMDD':
      return Messages.flowParametersTimestampFormat_YYYYMMDD();
    case 'YYYYMMDD_HHMMSS':
      return Messages.flowParametersTimestampFormat_YYYYMMDD_HHMMSS();
    case 'DD-MM-YYYY':
      return Messages.flowParametersTimestampFormat_DD_MM_YYYY();
    case 'Week Number':
      return Messages.flowParametersTimestampFormat_Week_Number();
    case 'Month Number':
      return Messages.flowParametersTimestampFormat_Month_Number();
    case 'Quarter Number':
      return Messages.flowParametersTimestampFormat_Quarter_Number();
    case 'Month Name':
      return Messages.flowParametersTimestampFormat_Month_Name();
    case 'Year Number':
      return Messages.flowParametersTimestampFormat_Year_Number();
    default:
      return null;
  }
}

export function isTimestampParameter(parameter: FlowParameter) {
  return parameter.domain.domainType === 'system' && parameter.domain.type === 'timestamp';
}

export function containsSystemTypeParameter(parameters: FlowParameter[]): boolean {
  return parameters.some(parameter => parameter.domain.domainType === 'system');
}
